import { FC, useEffect, useReducer, useRef, useState } from 'react';
import { debounce, isEmpty } from 'lodash';

import './AIAssistantEntireDaySettings.scss';
import {
  initialDefaultTimeboxPrefItemState,
  SchedulerTimeboxesPref,
  SchedulerWorkHourPref,
  timeboxPrefItemReducer,
  initialDefaultWorkHourPrefItemState,
  workHourPrefItemReducer,
  transformDBPrefToWorkHourPref,
  transformDBPrefToTimeboxPref,
  transformTimeboxPrefToDB,
  WorkHourPrefItemState,
  transformWorkHourPrefToDB,
  TimeboxPrefItem,
} from '../../../../Scheduler';
import { usePrepareScheduler } from '../../../../../data-access';
import {
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
  ClickAwayListener,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { SchedulerPreference, TimeboxSessionType } from '@demind-inc/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { CustomTooltip } from '../../../../common';

const BUFFER_MINUTE_OPTIONS = [0, 5, 10, 15, 20, 30, 45, 60];
const POMODORO_MINUTE_OPTIONS = [30, 45, 60, 90, 120];

const AIAssistantEntireDaySettings: FC = () => {
  const { t } = useTranslation();
  const { schedulerPreferences, updatePreferences, isPreferenceLoading } = usePrepareScheduler();

  const [workHourPrefItem, dispatchWorkHourPrefItem] = useReducer(
    workHourPrefItemReducer,
    initialDefaultWorkHourPrefItemState
  );
  const [timeBoxPrefItems, dispatchTimeBoxPrefItems] = useReducer(
    timeboxPrefItemReducer,
    initialDefaultTimeboxPrefItemState
  );
  const [bufferMinutes, setBufferMinutes] = useState<number>(0);
  const [bufferMenuOpen, setBufferMenuOpen] = useState(false);
  const bufferAnchorRef = useRef<HTMLDivElement>(null);
  const [pomodoroDurationMinutes, setPomodoroDurationMinutes] = useState<number>(90);
  const [pomodoroMenuOpen, setPomodoroMenuOpen] = useState(false);
  const pomodoroAnchorRef = useRef<HTMLDivElement>(null);

  // Initialize the preferences
  useEffect(() => {
    if (!schedulerPreferences || isEmpty(schedulerPreferences)) {
      return;
    }
    // Work hours and Timeboxes are stored in scheduler.preferences
    dispatchWorkHourPrefItem({
      type: 'initialize',
      payload: transformDBPrefToWorkHourPref(schedulerPreferences),
    });
    dispatchTimeBoxPrefItems({
      type: 'initialize',
      payload: transformDBPrefToTimeboxPref(schedulerPreferences),
    });
    setBufferMinutes(schedulerPreferences.bufferMinutes ?? 0);
    setPomodoroDurationMinutes(schedulerPreferences.pomodoro?.pomodoroDurationMin ?? 90);
  }, [schedulerPreferences]);

  const handleUpdateWorkHourPref = (pref: WorkHourPrefItemState) => {
    dispatchWorkHourPrefItem({ type: 'workTime', payload: pref });
    const workHours = transformWorkHourPrefToDB({
      ...workHourPrefItem,
      ...pref,
    });
    debounce(() => {
      updatePreferences(workHours);
    }, 1000)();
  };

  const handleUpdateTimeboxPref = (id: TimeboxSessionType, pref: TimeboxPrefItem) => {
    dispatchTimeBoxPrefItems({ type: id, payload: pref });
    const timeboxes = transformTimeboxPrefToDB({
      ...timeBoxPrefItems,
      [id]: pref,
    });
    debounce(() => {
      updatePreferences(timeboxes);
    }, 1000)();
  };

  const handleUpdateBufferMinutes = (minutes: number) => {
    setBufferMinutes(minutes);
    setBufferMenuOpen(false);
    debounce(() => {
      updatePreferences({ bufferMinutes: minutes });
    }, 1000)();
  };

  const handleUpdatePomodoroDuration = (minutes: number) => {
    setPomodoroDurationMinutes(minutes);
    setPomodoroMenuOpen(false);
    debounce(() => {
      updatePreferences({
        pomodoro: {
          ...schedulerPreferences?.pomodoro,
          pomodoroDurationMin: minutes,
        },
      } as Partial<SchedulerPreference>);
    }, 1000)();
  };

  const formatBufferMinutes = (minutes: number): string => {
    return t('settings.aiAssistant.minutesShort', { count: minutes });
  };

  return (
    <div className="ai-assistant-settings">
      {isPreferenceLoading ? (
        <CircularProgress />
      ) : (
        <div className="ai-assistant-settings__list">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="ai-assistant-settings__buffer"
          >
            <div className="ai-assistant-settings__buffer-text">
              <Typography variant="body2" className="ai-assistant-settings__buffer-title">
                {t('settings.aiAssistant.bufferBetweenTasks')}
              </Typography>
            </div>
            <ClickAwayListener
              onClickAway={() => {
                setBufferMenuOpen(false);
              }}
            >
              <div ref={bufferAnchorRef} className="ai-assistant-settings__buffer-select-container">
                <div
                  onClick={() => !isPreferenceLoading && setBufferMenuOpen(true)}
                  className={clsx(
                    'ai-assistant-settings__buffer-select',
                    isPreferenceLoading && 'ai-assistant-settings__buffer-select--disabled'
                  )}
                >
                  <Typography variant="body2" className="ai-assistant-settings__buffer-select-text">
                    {formatBufferMinutes(bufferMinutes)}
                  </Typography>
                </div>
              </div>
            </ClickAwayListener>
            <Menu
              anchorEl={bufferAnchorRef.current}
              open={bufferMenuOpen}
              onClose={() => setBufferMenuOpen(false)}
              className="ai-assistant-settings__buffer-menu"
              PaperProps={{
                className: 'ai-assistant-settings__buffer-menu-paper',
              }}
              MenuListProps={{
                className: 'ai-assistant-settings__buffer-menu-list',
              }}
            >
              {BUFFER_MINUTE_OPTIONS.map((minutes) => (
                <MenuItem
                  key={minutes}
                  onClick={() => handleUpdateBufferMinutes(minutes)}
                  className="ai-assistant-settings__buffer-menu-item"
                  selected={bufferMinutes === minutes}
                >
                  {formatBufferMinutes(minutes)}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="ai-assistant-settings__buffer"
          >
            <div className="ai-assistant-settings__buffer-text">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="body2" className="ai-assistant-settings__buffer-title">
                  {t('settings.aiAssistant.pomodoroDuration')}
                </Typography>
                <CustomTooltip title={t('settings.aiAssistant.pomodoroDurationTooltip')}>
                  <InfoOutlined className="ai-assistant-settings__info-icon" fontSize="small" />
                </CustomTooltip>
              </Stack>
            </div>
            <ClickAwayListener
              onClickAway={() => {
                setPomodoroMenuOpen(false);
              }}
            >
              <div
                ref={pomodoroAnchorRef}
                className="ai-assistant-settings__buffer-select-container"
              >
                <div
                  onClick={() => !isPreferenceLoading && setPomodoroMenuOpen(true)}
                  className={clsx(
                    'ai-assistant-settings__buffer-select',
                    isPreferenceLoading && 'ai-assistant-settings__buffer-select--disabled'
                  )}
                >
                  <Typography variant="body2" className="ai-assistant-settings__buffer-select-text">
                    {formatBufferMinutes(pomodoroDurationMinutes)}
                  </Typography>
                </div>
              </div>
            </ClickAwayListener>
            <Menu
              anchorEl={pomodoroAnchorRef.current}
              open={pomodoroMenuOpen}
              onClose={() => setPomodoroMenuOpen(false)}
              className="ai-assistant-settings__buffer-menu"
              PaperProps={{
                className: 'ai-assistant-settings__buffer-menu-paper',
              }}
              MenuListProps={{
                className: 'ai-assistant-settings__buffer-menu-list',
              }}
            >
              {POMODORO_MINUTE_OPTIONS.map((minutes) => (
                <MenuItem
                  key={minutes}
                  onClick={() => handleUpdatePomodoroDuration(minutes)}
                  className="ai-assistant-settings__buffer-menu-item"
                  selected={pomodoroDurationMinutes === minutes}
                >
                  {formatBufferMinutes(minutes)}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
          <SchedulerWorkHourPref
            isLoading={isPreferenceLoading}
            workHours={workHourPrefItem}
            onWorkHourChange={handleUpdateWorkHourPref}
            fontSize="small"
            className="ai-assistant-settings__work-hour-pref"
            isInSettingsModal
          />
          <SchedulerTimeboxesPref
            isLoading={isPreferenceLoading}
            timeboxes={timeBoxPrefItems}
            onTimeboxChange={handleUpdateTimeboxPref}
            fontSize="small"
            isInSettingsModal
          />
        </div>
      )}
    </div>
  );
};

export default AIAssistantEntireDaySettings;
